'use client';
/**
 * L'ONGLET « PRÉSENTATION » D'UN ÉQUIPEMENT (spec 002, récit 9 — proposition d'Ines, 03/11/2026).
 *
 * « Je clique sur contrat de maintenance de l'UriSed, j'atterris dans l'Identification » ; « un seul onglet …
 * Présentation ». Identification et Mise en service ne font plus qu'un onglet, et le parcours d'accueil n'y est plus
 * une liste qui renvoie ailleurs : il est L'ORDRE DES BLOCS. Chaque bloc porte sa pastille (fait, à faire, sans objet)
 * et se complète SUR PLACE, dans une fenêtre qui n'envoie que ses champs (`SaisieBloc`). En haut, le bandeau
 * d'avancement nomme les points manquants ; chacun fait descendre jusqu'à son bloc, mis en évidence.
 *
 * Ordre : (dossier de mise en service tant que l'appareil n'est pas autorisé) · Achat · Équipement · Personnel ·
 * Réactifs & examens · (dossier de mise en service replié, une fois autorisé) · Réforme, Observation.
 *
 * Adresse : `/equipements/<code>?onglet=presentation&bloc=<bloc>` (défile et met en évidence), `&saisie=1` (ouvre la
 * saisie du bloc). Les anciennes `?onglet=identification` et `?onglet=mes` y mènent (`redirectionOngletEquipement`).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useGed, userName } from '@/lib/store';
import { card, Pastille, Modale, inputStyle } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { Tableau } from '@/shared/Tableau';
import { fetchEcriture } from '@/lib/ecrire';
import { fmtDate, natureEquip, CATEGORIE_LOGICIEL, exigeMetrologie } from '@/lib/meta';
import { peutGererParc } from '@/lib/droits-parc';
import { etatBloc, type BlocPresentation, type EtatPoint, type Pan } from '@/lib/accueil-equipement';
import type { GedEquipement } from '@/lib/types';
import { AlertesFiche, BlocNature, ContenuBloc, SaisieBloc, estBlocSaisie, type BlocSaisie } from './EquipFiche';
import { BlocContacts } from './ContactsEquipement';
import { MiseEnService } from './MiseEnService';
import { usePansAccueil, BandeauAvancement, ETAT_POINT } from './ParcoursAccueil';
import { lbl } from './commun';

export interface CiblePresentation { bloc?: BlocPresentation; saisie?: boolean }

const estLogiciel = (e: GedEquipement) => natureEquip(e.nature).key === 'logiciel' || String(e.categorie || '').trim() === CATEGORIE_LOGICIEL;
/** Même règle que le parcours : seul un appareil de MESURE passe des réactifs et rend des examens. */
const produitUnResultat = (e: GedEquipement) => (String(e.nature || '').trim() || 'mesure') === 'mesure';

/**
 * L'état d'un bloc : celui des points du parcours qui le désignent ; à défaut, une lecture locale de la fiche pour
 * les blocs qu'aucun point ne couvre. `null` : le bloc n'a pas d'état à dire (les postes se DÉDUISENT des
 * habilitations ; une observation n'est jamais « à faire »).
 */
function etatDuBloc(e: GedEquipement, pans: Pan[], bloc: BlocPresentation): EtatPoint | null {
  const duParcours = etatBloc(pans, bloc);
  if (duParcours) return duParcours;
  switch (bloc) {
    case 'alimentation': return estLogiciel(e) ? 'sans_objet' : (e.voltage || e.amperage || e.puissance) ? 'fait' : 'afaire';
    case 'logiciel': return estLogiciel(e) ? (e.logiciel?.version ? 'fait' : 'afaire') : (e.sil?.relie ? 'fait' : 'afaire');
    case 'metrologie': return e.metrologie?.raccordement ? 'fait' : 'afaire';
    // Pas d'état « réformé » au parc : le bloc n'a d'objet que lorsqu'une réforme est écrite.
    case 'reforme': return (e.reforme?.date || e.reforme?.lieu || e.reforme?.rapportPar) ? 'fait' : 'sans_objet';
    default: return null;
  }
}

export function Presentation({ e, onOnglet, cible }: {
  e: GedEquipement;
  /** Mène à un autre onglet du dossier (Maintenance, Examens) — on ne quitte jamais le dossier. */
  onOnglet: (onglet: 'maintenance' | 'examens') => void;
  cible?: CiblePresentation;
}) {
  const { session, settings } = useGed();
  const peutParc = peutGererParc(session.roles);
  /* Relire ce qui vient du serveur (habilitations, catalogue) après une saisie faite ici. */
  const [versionLecture, setVersionLecture] = useState(0);
  const [versionHab, setVersionHab] = useState(0);
  const { pans, produits } = usePansAccueil(e, versionLecture);
  const [saisie, setSaisie] = useState<{ bloc: BlocSaisie; proposition?: string } | null>(
    () => (cible?.saisie && cible.bloc && estBlocSaisie(cible.bloc) && peutParc ? { bloc: cible.bloc } : null));
  const [reactifs, setReactifs] = useState(false);
  const [enEvidence, setEnEvidence] = useState<BlocPresentation | null>(cible?.bloc ?? null);
  const refs = useRef<Partial<Record<BlocPresentation, HTMLElement | null>>>({});
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);

  const eteindre = () => {
    if (minuterie.current) clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => setEnEvidence(null), 2600);
  };
  const allerAu = (bloc: BlocPresentation) => {
    refs.current[bloc]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setEnEvidence(bloc);
    eteindre();
  };
  /* L'adresse `&bloc=` : on descend jusqu'au bloc à l'arrivée (le bloc est déjà mis en évidence par l'état initial). */
  useEffect(() => {
    if (!cible?.bloc) return;
    const bloc = cible.bloc;
    const t = setTimeout(() => {
      refs.current[bloc]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      eteindre();
    }, 60);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  useEffect(() => () => { if (minuterie.current) clearTimeout(minuterie.current); }, []);

  const logiciel = estLogiciel(e);
  const mesure = produitUnResultat(e);
  const autorise = !!e.miseEnService?.autoriseLe;
  const pointsAnalytiques = pans.find((p) => p.cle === 'analytique')?.points || [];
  const pointPlan = pans.find((p) => p.cle === 'equipement')?.points.find((x) => x.cle === 'maintenance');

  /** Un bloc de la page : son titre, sa pastille, son bouton de saisie, son contenu. */
  const carte = (bloc: BlocPresentation, titre: string, contenu: React.ReactNode, opts: { saisie?: BlocSaisie | null; actions?: React.ReactNode } = {}) => {
    const etat = etatDuBloc(e, pans, bloc);
    const m = etat ? ETAT_POINT[etat] : null;
    const saisieBloc = opts.saisie === undefined ? (estBlocSaisie(bloc) ? bloc : null) : opts.saisie;
    return (
      <section key={bloc} id={`bloc-${bloc}`} ref={(n) => { refs.current[bloc] = n; }} aria-label={titre}
        style={{
          ...card, padding: 14, display: 'grid', gap: 10, scrollMarginTop: 12,
          outline: enEvidence === bloc ? '2px solid var(--color-primary)' : undefined, outlineOffset: 2,
          transition: 'outline-color .3s',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</h3>
          {m && <Pastille ton={m.ton} compacte>{m.libelle}</Pastille>}
          <span style={{ flex: 1 }} />
          {opts.actions}
          {peutParc && saisieBloc && (
            <Bouton taille="sm" variante={etat === 'afaire' ? 'primaire' : undefined} onClick={() => setSaisie({ bloc: saisieBloc })}>
              {etat === 'afaire' ? 'Compléter' : 'Modifier'}
            </Bouton>
          )}
        </div>
        {contenu}
      </section>
    );
  };
  const groupe = (titre: string, blocs: React.ReactNode) => (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ ...lbl, marginBottom: 0, fontSize: 11.5 }}>{titre}</div>
      {blocs}
    </div>
  );
  const contenu = (bloc: BlocPresentation) => (
    <ContenuBloc e={e} bloc={bloc} versionHab={versionHab}
      onHabilitations={() => { setVersionHab((v) => v + 1); setVersionLecture((v) => v + 1); }} />
  );

  const dossierMes = carte('mise-en-service', 'Dossier de mise en service', <MiseEnService e={e} />, { saisie: null });

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <BandeauAvancement pans={pans} onBloc={allerAu} />
      <AlertesFiche e={e} />
      {/* Spec 002, T099 (EF-040) : l'état « indisponible » de l'appareil est dit
          avec son effet — aucun sur le plan de maintenance. */}
      {e.statut === 'maintenance' && (
        <p role="note" style={{ ...card, margin: 0, padding: '10px 14px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong>Indisponible (intervention en cours).</strong> L’appareil ne produit pas de résultat le temps
          de l’intervention. Ce statut ne suspend pas les échéances du plan de maintenance : elles restent dues.
          Seule une mise hors service les suspend.
        </p>
      )}

      {/* Tant que l'appareil n'est pas autorisé, son dossier est ce qu'on vient faire : il passe en tête. */}
      {!autorise && dossierMes}

      {groupe('Achat', <>
        {carte('signaletique', 'Signalétique', <div style={{ display: 'grid', gap: 12 }}><BlocNature e={e} />{contenu('signaletique')}</div>)}
        {carte('fournisseur', 'Fournisseur', contenu('fournisseur'))}
        {carte('acquisition', 'Acquisition & garantie', contenu('acquisition'))}
        {carte('contrat', 'Contrat de maintenance', contenu('contrat'))}
        {carte('contacts', 'Contacts', <BlocContacts e={e} onRelier={peutParc ? (code) => setSaisie({ bloc: 'contacts', proposition: code }) : undefined} />)}
      </>)}

      {groupe('Équipement', <>
        {!logiciel && carte('alimentation', 'Alimentation', contenu('alimentation'))}
        {carte('documentation', 'Documentation & formation', contenu('documentation'))}
        {carte('logiciel', logiciel ? 'Logiciel : version, postes, SIL' : 'Logiciel embarqué & SIL', contenu('logiciel'))}
        {exigeMetrologie(e.categorie) && carte('metrologie', 'Métrologie', contenu('metrologie'))}
        {carte('plan', 'Plan de maintenance', (
          <div style={{ display: 'grid', gap: 6, fontSize: 12.5 }}>
            {pointPlan && <div style={{ color: 'var(--text-body)' }}>{pointPlan.detail}</div>}
            <div style={{ color: 'var(--text-muted)' }}>
              {/* EF-026 : trois valeurs, le même sens que le calcul — absente = « non déclaré ». */}
              {e.maintenanceJoursNonOuvres === false
                ? 'Une échéance tombant un dimanche ou un jour férié est déplacée (politique de report du paramétrage).'
                : e.maintenanceJoursNonOuvres === true
                  ? 'Les maintenances ont lieu les jours non ouvrés : aucune échéance n’est déplacée.'
                  : 'Jours non ouvrés : non déclaré — la politique de report de la ligne, de la fréquence ou du paramétrage s’applique.'}
            </div>
          </div>
        ), { actions: <Bouton taille="sm" variante="discret" onClick={() => onOnglet('maintenance')}>Onglet Maintenance</Bouton> })}
      </>)}

      {groupe('Personnel', <>
        {carte('responsables', 'Responsable et suppléant', contenu('responsables'))}
        {carte('habilitations', 'Personnes habilitées', contenu('habilitations'))}
        {carte('postes', 'Postes de travail', contenu('postes'))}
      </>)}

      {mesure && groupe('Réactifs & examens', carte('analytique', 'Réactifs & examens', (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gap: 4 }}>
            {pointsAnalytiques.map((pt) => (
              <div key={pt.cle} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
                <strong style={{ color: 'var(--text-strong)' }}>{pt.libelle}</strong>
                <Pastille ton={ETAT_POINT[pt.etat].ton} compacte>{ETAT_POINT[pt.etat].libelle}</Pastille>
                <span style={{ color: 'var(--text-muted)' }}>{pt.detail}</span>
              </div>
            ))}
          </div>
          {produits === null
            ? <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture du catalogue…</div>
            : (
              <Tableau
                id="reactifs-de-l-appareil"
                ajusterLargeur
                lignes={produits}
                cleLigne={(p) => p.code}
                vide={{ titre: 'Aucun produit du catalogue n’a cet appareil pour appareil par défaut.', indice: '« Relier des réactifs » les choisit dans le catalogue.' }}
                colonnes={[
                  { cle: 'libelle', libelle: 'Produit du catalogue', valeur: (p) => p.libelle, style: { whiteSpace: 'normal' }, rendu: (p) => p.libelle },
                  { cle: 'examens', libelle: 'Examens', type: 'nombre', valeur: (p) => p.nbExamens, rendu: (p) => p.nbExamens },
                ]}
              />
            )}
        </div>
      ), {
        saisie: null,
        actions: <>
          <Bouton taille="sm" onClick={() => setReactifs(true)}>Relier des réactifs</Bouton>
          <Bouton taille="sm" variante="discret" onClick={() => onOnglet('examens')}>Onglet Examens</Bouton>
        </>,
      }))}

      {autorise && (
        <section id="bloc-mise-en-service" ref={(n) => { refs.current['mise-en-service'] = n; }}
          style={{ ...card, padding: 14, scrollMarginTop: 12, outline: enEvidence === 'mise-en-service' ? '2px solid var(--color-primary)' : undefined, outlineOffset: 2 }}>
          {/* Une fois autorisé, le dossier se replie en une ligne : il atteste, il ne se travaille plus. */}
          <details open={enEvidence === 'mise-en-service' || undefined}>
            <summary style={{ cursor: 'pointer', fontSize: 12.5, color: 'var(--text-body)' }}>
              <strong>Mis en service le {fmtDate(e.miseEnService?.autoriseLe)}</strong>
              {e.miseEnService?.autorisePar ? <>, autorisé par {userName(settings, e.miseEnService.autorisePar)}</> : null}
              <span style={{ color: 'var(--text-muted)' }}> — dossier de mise en service</span>
            </summary>
            <div style={{ marginTop: 12 }}><MiseEnService e={e} /></div>
          </details>
        </section>
      )}

      {groupe('Fin de vie et observation', <>
        {carte('reforme', 'Mise en réforme', contenu('reforme'))}
        {carte('observation', 'Observation', contenu('observation'))}
      </>)}

      {saisie && <SaisieBloc e={e} bloc={saisie.bloc} proposition={saisie.proposition} onFerme={() => setSaisie(null)} />}
      {reactifs && <RelierReactifs e={e} onFerme={() => setReactifs(false)} onFait={() => setVersionLecture((v) => v + 1)} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * « RELIER DES RÉACTIFS À CET APPAREIL » (spec 002, récit 9, T044)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Le point « Réactifs reliés » menait au Catalogue des produits, sans emporter l'appareil ni chemin de retour. On
 * choisit ici les réactifs, calibrants et contrôles du catalogue ; chacun reçoit cet appareil pour appareil par
 * défaut (`equipementCode`) par la route du catalogue (`POST ged/produits/:code`), avec la lecture jointe (`base`) :
 * le moteur n'écrit que ce champ. Mêmes droits que le catalogue — le serveur le dit (`peutEcrire`).
 */
type LigneProduit = {
  code: string; libelle: string; famille: string; equipementCode: string; actif: boolean; fournisseurNom?: string;
  [k: string]: unknown;
};
const FAMILLES_REACTIF: Record<string, string> = { reactif: 'Réactif', calibrant: 'Calibrant', controle: 'Matériau de contrôle' };

function RelierReactifs({ e, onFerme, onFait }: { e: GedEquipement; onFerme: () => void; onFait: () => void }) {
  const { showToast, equips } = useGed();
  const code = e.code || '';
  const [lu, setLu] = useState<{ lignes: LigneProduit[]; peutEcrire: boolean } | null>(null);
  const [erreurLecture, setErreurLecture] = useState('');
  const [choisis, setChoisis] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [refus, setRefus] = useState<string[]>([]);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/produits', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant) return;
        if (!j) { setErreurLecture('Le catalogue n’a pas pu être lu.'); return; }
        setLu({ lignes: (j.lignes || []) as LigneProduit[], peutEcrire: j.peutEcrire === true });
      })
      .catch(() => { if (vivant) setErreurLecture('Le catalogue n’a pas pu être lu.'); });
    return () => { vivant = false; };
  }, []);

  const nomAppareil = (c: string) => (equips || []).find((x) => x.code === c)?.nom || c;
  const sansAccent = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const offerts = (lu?.lignes || [])
    .filter((p) => p.actif && FAMILLES_REACTIF[p.famille] && p.equipementCode !== code)
    .filter((p) => !q.trim() || sansAccent(`${p.libelle} ${p.fournisseurNom || ''}`).includes(sansAccent(q.trim())));
  const basculer = (c: string) => setChoisis((l) => (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]));

  const relier = async () => {
    if (!lu) return;
    setEnCours(true); setRefus([]);
    const echecs: string[] = [];
    const enEchec: string[] = [];
    let faits = 0;
    for (const c of choisis) {
      const p = lu.lignes.find((x) => x.code === c);
      if (!p) continue;
      const r = await fetchEcriture(`/api/ged/ged/produits/${encodeURIComponent(p.code)}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...p, equipementCode: code, base: p }),
      });
      const j = await r.json().catch(() => null);
      if (r.ok) faits += 1;
      else { echecs.push(`${p.libelle} : ${j?.error?.message || 'refusé'}`); enEchec.push(c); }
    }
    setEnCours(false);
    if (faits) { showToast(`${faits} produit${faits > 1 ? 's' : ''} relié${faits > 1 ? 's' : ''} à ${code}.`); onFait(); }
    // Ce qui a été refusé reste coché et nommé : on corrige, on réessaie, sans tout reprendre.
    if (echecs.length) { setRefus(echecs); setChoisis(enEchec); return; }
    onFerme();
  };

  return (
    <Modale titre={`Relier des réactifs à ${code || e.nom}`} onClose={onFerme} largeur={820}
      pied={lu?.peutEcrire ? (
        <>
          <Bouton onClick={onFerme} disabled={enCours}>Annuler</Bouton>
          <Bouton variante="primaire" disabled={!choisis.length || enCours} onClick={() => void relier()}>
            {enCours ? 'Enregistrement…' : choisis.length ? `Relier ${choisis.length} produit${choisis.length > 1 ? 's' : ''}` : 'Relier'}
          </Bouton>
        </>
      ) : undefined}>
      <div style={{ display: 'grid', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Réactifs, calibrants et contrôles du catalogue. Chaque produit choisi aura cet appareil pour appareil par
          défaut : la réception d’un lot le proposera. Un produit déjà relié à un autre appareil changera d’appareil.
        </p>
        {erreurLecture && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)' }}>{erreurLecture}</p>}
        {lu && !lu.peutEcrire && (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)' }}>Le catalogue se modifie par le RQ et les achats : la liste est en lecture.</p>
        )}
        {refus.length > 0 && (
          <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, color: 'var(--red-600)' }}>
            {refus.map((r) => <li key={r}>{r}</li>)}
          </ul>
        )}
        {!lu && !erreurLecture && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture du catalogue…</div>}
        {lu && (
          <>
            <input aria-label="Chercher un produit" placeholder="Chercher un produit, un fournisseur…" value={q}
              onChange={(ev) => setQ(ev.target.value)} style={inputStyle} />
            <Tableau
              id="relier-reactifs-appareil"
              ajusterLargeur
              lignes={offerts}
              cleLigne={(p) => p.code}
              vide={{ titre: q.trim() ? 'Aucun produit ne correspond.' : 'Tous les réactifs, calibrants et contrôles actifs du catalogue sont déjà reliés à cet appareil.' }}
              colonnes={[
                { cle: 'choix', libelle: '', rendu: (p) => (
                  <input type="checkbox" aria-label={`Choisir ${p.libelle}`} checked={choisis.includes(p.code)}
                    disabled={!lu.peutEcrire || enCours} onChange={() => basculer(p.code)} />
                ) },
                { cle: 'libelle', libelle: 'Produit', valeur: (p) => p.libelle, style: { whiteSpace: 'normal' }, rendu: (p) => p.libelle },
                { cle: 'famille', libelle: 'Famille', type: 'liste', valeur: (p) => FAMILLES_REACTIF[p.famille], rendu: (p) => FAMILLES_REACTIF[p.famille] },
                { cle: 'fournisseur', libelle: 'Fournisseur', valeur: (p) => p.fournisseurNom || '', rendu: (p) => p.fournisseurNom || '' },
                { cle: 'appareil', libelle: 'Appareil actuel', valeur: (p) => p.equipementCode || '', style: { whiteSpace: 'normal' },
                  rendu: (p) => (p.equipementCode ? `${p.equipementCode} — ${nomAppareil(p.equipementCode)}` : <span style={{ color: 'var(--text-muted)' }}>aucun</span>) },
              ]}
            />
          </>
        )}
      </div>
    </Modale>
  );
}
